// The template editor's content card: one layout choice, plus the flat,
// reorderable list of pieces a screen shows.
//
// Two shapes only. A stack lays every piece out on its own line, top to
// bottom — the tall 4:3 screen: logo, sentence, number. A band lays every
// piece on one line, side by side — the 1222x70 strip: logo, sentence,
// number, logo. An earlier design let pieces nest into lines of their own
// and merge or split across them; it was rejected as far more machinery than
// two shapes need. This stays a flat list plus one radio-like choice on
// purpose — resist the urge to add grouping, nesting, or a second ordering.
//
// Every gesture has a button. Dragging the grip reorders a row, but drag
// alone is close to unusable by keyboard, so move-up/move-down buttons are
// the accessible path, not a nice-to-have, and they carry the real
// `aria-label`s a screen reader announces.
//
// One code path. Buttons and drags both resolve to a full next-array and
// hand it to the field array's single `replace` call — never `move`,
// `insert`, or `remove` directly — so the two ways of reordering cannot race
// each other or diverge in behaviour.
//
// Plain language only: the operator reads "piece", "line", "wide strip", and
// the two axis names "Column"/"Row" — never "flex", "section key", or a repo
// path. The one exception is the id chip's copied text, a CSS attribute
// selector meant for whoever writes the template's CSS.

import {
    closestCenter,
    DndContext,
    type DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Card, CopyButton, Input, SegmentedControl, Select, Textarea } from "@pej/kore/components";
import { Clock, GripVertical, Image as ImageIcon, MoveDown, MoveUp, Trash2, Type as TypeIcon } from "lucide-react";
import type { Control } from "react-hook-form";
import { RangeControl } from "@/components/range-control";
import { Field, useFieldArray, useWatch } from "@/lib/forms";
import { ArtworkSlot } from "./artwork-field";
import { DEFAULT_SECTION_SIZE, nextSectionId, type SectionValues, type TemplateValues } from "./template-schema";

interface ContentSectionProps {
    control: Control<TemplateValues>;
}

const TYPE_ICON: Record<SectionValues["type"], typeof TypeIcon> = {
    text: TypeIcon,
    image: ImageIcon,
    metric: Clock,
};

const TYPE_TITLE: Record<SectionValues["type"], string> = {
    text: "Text",
    image: "Image",
    metric: "Waiting time",
};

/** Reorders `sections`, moving the item at `from` to `to`. Pure and
 *  bounds-checked: an out-of-range index leaves the list exactly as it was,
 *  the same instance, so callers can compare by reference if they want to. */
export function moveSection(sections: SectionValues[], from: number, to: number): SectionValues[] {
    if (from < 0 || from >= sections.length || to < 0 || to >= sections.length) return sections;
    const next = [...sections];
    const moved = next[from];
    // Unreachable given the bounds check above — `from` is a valid index —
    // but satisfies noUncheckedIndexedAccess without a non-null assertion.
    if (!moved) return sections;
    next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
}

/** A freshly minted piece of the given type, in form state — empty of its
 *  own content. */
function newSection(type: SectionValues["type"], id: string): SectionValues {
    switch (type) {
        case "text":
            return { type: "text", id, text: "", size: DEFAULT_SECTION_SIZE };
        case "image":
            return { type: "image", id, assetId: "" };
        case "metric":
            return { type: "metric", id, unit: "", size: DEFAULT_SECTION_SIZE };
    }
}

/** Option labels for the template-level Distribution control. `center` and
 *  `spread` are layout-neutral; `start`/`end` read as edges of whichever axis
 *  Layout currently chooses, so the same value shows as "Top"/"Bottom" in a
 *  Column and "Left"/"Right" in a Row. */
function distributionLabel(value: TemplateValues["distribution"], layout: TemplateValues["layout"]): string {
    switch (value) {
        case "center":
            return "Centered";
        case "spread":
            return "Spread out";
        case "start":
            return layout === "column" ? "Top" : "Left";
        case "end":
            return layout === "column" ? "Bottom" : "Right";
    }
}

export function ContentSection({ control }: ContentSectionProps) {
    // `keyName: "key"` keeps this array's own `id` field (the CSS handle,
    // "s1"/"s2"/…) intact on each row — react-hook-form's default behaviour
    // is to overwrite an item's existing `id` with its own bookkeeping key,
    // which would otherwise clobber the one thing this list must never
    // silently change.
    const { fields, replace } = useFieldArray({ control, name: "sections", keyName: "key" });
    // `fields` only reflects a row's shape (its type and its id never change
    // after creation) — a row's authored content is read live from the form
    // instead, so an edit made a moment ago is never lost under an add,
    // remove, or reorder that rebuilds the whole array.
    const sections = useWatch({ control, name: "sections" }) ?? [];
    // Drives the Distribution control's labels, which read as edges of
    // whichever axis Layout currently chooses.
    const layout = useWatch({ control, name: "layout" });
    const hasMetric = fields.some((field) => field.type === "metric");

    const sensors = useSensors(
        // A short movement threshold before a drag starts, so a click or tap
        // on a control inside the row (the text box, the remove button)
        // reaches that control instead of lifting the row.
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const ids = fields.map((field) => field.id);
        const from = ids.indexOf(String(active.id));
        const to = ids.indexOf(String(over.id));
        replace(moveSection(sections, from, to));
    }

    function handleMove(index: number, direction: -1 | 1) {
        replace(moveSection(sections, index, index + direction));
    }

    function handleRemove(index: number) {
        replace(sections.filter((_, i) => i !== index));
    }

    function handleAdd(type: SectionValues["type"]) {
        replace([...sections, newSection(type, nextSectionId(sections))]);
    }

    return (
        <Card className="p-6">
            <h2 className="text-base font-semibold">Content</h2>
            <p className="mt-1 text-sm text-muted-foreground">
                The pieces on the screen — text, an image, or the waiting-time number — and how they're arranged.
            </p>
            <div className="mt-4 flex flex-col gap-4">
                <div className="flex flex-wrap gap-4">
                    <Field
                        name="layout"
                        control={control}
                        description="Column puts each piece on its own line. Row puts everything side by side — for a wide, short strip."
                        render={({ field: { value, onChange } }) => (
                            <SegmentedControl
                                aria-label="Layout"
                                value={value}
                                onValueChange={onChange}
                                options={[
                                    { value: "column", label: "Column" },
                                    { value: "row", label: "Row" },
                                ]}
                            />
                        )}
                    />

                    <Field
                        name="distribution"
                        control={control}
                        description="Where the pieces sit along that line."
                        render={({ field: { value, onChange, name } }) => (
                            <Select value={value} onValueChange={onChange} name={name}>
                                <Select.Trigger aria-label="Distribution">
                                    <Select.Value>
                                        {(v: unknown) => distributionLabel(v as TemplateValues["distribution"], layout)}
                                    </Select.Value>
                                </Select.Trigger>
                                <Select.Content>
                                    <Select.Item value="center">{distributionLabel("center", layout)}</Select.Item>
                                    <Select.Item value="spread">{distributionLabel("spread", layout)}</Select.Item>
                                    <Select.Item value="start">{distributionLabel("start", layout)}</Select.Item>
                                    <Select.Item value="end">{distributionLabel("end", layout)}</Select.Item>
                                </Select.Content>
                            </Select>
                        )}
                    />
                </div>

                {fields.length > 0 && (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={fields.map((field) => field.id)} strategy={verticalListSortingStrategy}>
                            <ul className="flex flex-col gap-2">
                                {fields.map((field, index) => (
                                    <SectionRow
                                        key={field.id}
                                        control={control}
                                        id={field.id}
                                        type={field.type}
                                        index={index}
                                        total={fields.length}
                                        onMove={handleMove}
                                        onRemove={handleRemove}
                                    />
                                ))}
                            </ul>
                        </SortableContext>
                    </DndContext>
                )}

                <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => handleAdd("text")}>
                        Add text
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => handleAdd("image")}>
                        Add image
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={hasMetric}
                        onClick={() => handleAdd("metric")}
                    >
                        Add waiting time
                    </Button>
                </div>
            </div>
        </Card>
    );
}

interface SectionRowProps {
    control: Control<TemplateValues>;
    id: string;
    type: SectionValues["type"];
    index: number;
    total: number;
    onMove: (index: number, direction: -1 | 1) => void;
    onRemove: (index: number) => void;
}

function SectionRow({ control, id, type, index, total, onMove, onRemove }: SectionRowProps) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 1 : undefined,
    };
    const Icon = TYPE_ICON[type];

    return (
        <li
            ref={setNodeRef}
            style={style}
            className="flex flex-wrap items-center gap-3 rounded-md border border-input bg-background p-3"
        >
            {/* The grip is the only draggable target on the row — everything
                else is an ordinary control, so no click inside the row has to
                fight a drag listener to register. */}
            <button
                type="button"
                {...attributes}
                {...listeners}
                aria-label="Reorder"
                title={`${TYPE_TITLE[type]} piece`}
                className="touch-none inline-flex size-7 shrink-0 cursor-grab items-center justify-center rounded border-0 bg-transparent text-muted-foreground active:cursor-grabbing"
            >
                <GripVertical className="size-4" aria-hidden />
            </button>

            <span
                aria-hidden
                title={`${TYPE_TITLE[type]} piece`}
                className="inline-flex size-6 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground"
            >
                <Icon className="size-3.5" />
            </span>

            <div className="flex shrink-0 flex-col">
                <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Move up"
                    disabled={index === 0}
                    onClick={() => onMove(index, -1)}
                >
                    <MoveUp className="size-3.5" aria-hidden />
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Move down"
                    disabled={index === total - 1}
                    onClick={() => onMove(index, 1)}
                >
                    <MoveDown className="size-3.5" aria-hidden />
                </Button>
            </div>

            <div className="min-w-0 flex-1">
                {type === "text" && (
                    <div className="flex flex-col gap-3">
                        <Field
                            name={`sections.${index}.text`}
                            control={control}
                            render={({ field }) => (
                                <Textarea
                                    {...field}
                                    rows={2}
                                    placeholder="Type the words for this piece…"
                                    aria-label="Text"
                                />
                            )}
                        />
                        <Field
                            name={`sections.${index}.size`}
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <RangeControl
                                    id={`section-size-${id}`}
                                    label="Size"
                                    value={Number(value)}
                                    onChange={onChange}
                                    min={50}
                                    max={200}
                                    step={5}
                                    defaultValue={DEFAULT_SECTION_SIZE}
                                />
                            )}
                        />
                    </div>
                )}
                {type === "image" && (
                    <Field
                        name={`sections.${index}.assetId`}
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <ArtworkSlot
                                assetType="IMAGE"
                                value={String(value)}
                                onChange={onChange}
                                fieldLabel="Image"
                                pickerTitle="Choose an image"
                                unavailableLabel="Image"
                            />
                        )}
                    />
                )}
                {type === "metric" && (
                    <div className="flex flex-col gap-3">
                        <Field
                            name={`sections.${index}.unit`}
                            control={control}
                            label="Unit label"
                            render={({ field }) => <Input {...field} placeholder="min" />}
                        />
                        <Field
                            name={`sections.${index}.size`}
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <RangeControl
                                    id={`section-size-${id}`}
                                    label="Size"
                                    value={Number(value)}
                                    onChange={onChange}
                                    min={50}
                                    max={200}
                                    step={5}
                                    defaultValue={DEFAULT_SECTION_SIZE}
                                />
                            )}
                        />
                    </div>
                )}
            </div>

            <span
                className="inline-flex shrink-0 items-center gap-1 text-muted-foreground"
                title="Copy the CSS handle for this piece"
            >
                <CopyButton value={`[data-section="${id}"]`} label={`Copy #${id}`} size="sm" />
                <span aria-hidden className="font-mono text-xs">
                    #{id}
                </span>
            </span>

            <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Remove"
                className="shrink-0"
                onClick={() => onRemove(index)}
            >
                <Trash2 className="size-4" aria-hidden />
            </Button>
        </li>
    );
}
